import { createContext, useContext, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { UserPicker, type UserPickerOption } from '@/components/ui/user-picker';

import { DeleteAuthConfirmDialog } from '@/features/admin-auth/components/DeleteAuthConfirmDialog';
import { FederatedIdentitiesTable } from '@/features/admin-federated-identities/components/FederatedIdentitiesTable';
import { LinkIdentityDialog } from '@/features/admin-federated-identities/components/LinkIdentityDialog';
import type { FederatedIdentityViewModel } from '@/features/admin-federated-identities/api';
import type { FederatedIdentityStatusDto } from '@/features/admin-federated-identities/endpoints';

import {
  isTenantAdminAccessDenied,
  useTenantFederatedIdentities,
  useTenantFederatedIdentityMutations,
  useTenantUserSearch,
} from '../api';

const STATUS_TABS: readonly FederatedIdentityStatusDto[] = ['PENDING', 'ACTIVE', 'RETIRED'];

/**
 * The linked user has no per-user profile page in the global console (PTV2-1472 decision), so the
 * table renders the linked user as plain text — the table opts out of a link by returning null.
 */
const NO_USER_HREF = (): null => null;

/**
 * Injects the tenant into the shared {@link LinkIdentityDialog}'s user typeahead: the dialog owns its
 * own query state and calls {@code useUserSearch(query)}, so we bind the tenant through context (rather
 * than an inline arrow, which would violate the rules of hooks) and read it in a real custom hook.
 */
const LinkPickerTenantContext = createContext<string>('');

function useTenantLinkPickerSearch(query: string): {
  readonly results: readonly UserPickerOption[];
  readonly isLoading: boolean;
} {
  const tenantUuid = useContext(LinkPickerTenantContext);
  return useTenantUserSearch(tenantUuid, query);
}

/**
 * "Federated identities" section of the tenant record page in the global console (PTV2-1472). Lets a
 * control-plane admin triage a tenant's captured external identities — link a PENDING subject to a
 * tenant user (so a tenant admin logging in via an external OAuth app is recognised on first login),
 * or unlink one. The tenant-plane analogue of {@code AdminFederatedIdentitiesPage}; reuses its
 * presentational parts (table, link dialog, confirm dialog) but points every hook at the tenant-admin
 * reach path. Copy is shared under {@code routes.platform.federatedIdentities.*}.
 *
 * A 404 from the scoped list means the caller lacks management access to THIS tenant (anti-enumeration)
 * — rendered as an access-denied message, exactly like {@code TenantUsersPanel}.
 */
export function TenantFederatedIdentitiesPanel({
  tenantUuid,
}: {
  readonly tenantUuid: string;
}): ReactElement {
  const { t } = useTranslation();
  const fp = 'routes.platform.federatedIdentities';

  const [status, setStatus] = useState<FederatedIdentityStatusDto>('PENDING');
  // A user filter only makes sense once identities have a linked user, so it's offered on the
  // Linked/Retired tabs, not on Pending.
  const showUserFilter = status !== 'PENDING';
  const [userFilter, setUserFilter] = useState<UserPickerOption | null>(null);
  const [userQuery, setUserQuery] = useState('');
  const { results: userResults, isLoading: userSearching } = useTenantUserSearch(tenantUuid, userQuery);

  const activeUserGuid = showUserFilter && userFilter !== null ? userFilter.guid : undefined;
  const query = useTenantFederatedIdentities(
    tenantUuid,
    activeUserGuid !== undefined ? { status, userGuid: activeUserGuid } : { status },
  );
  const { link, unlink } = useTenantFederatedIdentityMutations(tenantUuid);

  const [linkTarget, setLinkTarget] = useState<FederatedIdentityViewModel | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<FederatedIdentityViewModel | null>(null);

  function selectStatus(next: FederatedIdentityStatusDto): void {
    setStatus(next);
    if (next === 'PENDING') {
      setUserFilter(null);
      setUserQuery('');
    }
  }

  const rows = useMemo(
    () =>
      query.isSuccess
        ? [...query.data].sort((a, b) =>
            (a.capturedName || a.capturedEmail || a.externalSubject).localeCompare(
              b.capturedName || b.capturedEmail || b.externalSubject,
            ),
          )
        : [],
    [query.isSuccess, query.data],
  );

  function closeLink(): void {
    setLinkTarget(null);
    link.reset();
  }

  function closeUnlink(): void {
    setUnlinkTarget(null);
    unlink.reset();
  }

  const unlinkLabel =
    unlinkTarget === null ? '' : unlinkTarget.capturedEmail || unlinkTarget.externalSubject;

  if (query.isError && isTenantAdminAccessDenied(query.error)) {
    return (
      <p role="alert" className="max-w-xl text-sm text-muted-foreground">
        {t('routes.platform.detail.users.accessDenied')}
      </p>
    );
  }

  return (
    <LinkPickerTenantContext.Provider value={tenantUuid}>
      <section aria-labelledby="tenant-federated-identities-heading" className="space-y-4">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h2
              id="tenant-federated-identities-heading"
              className="text-base font-semibold text-foreground"
            >
              {t(`${fp}.listHeading`)}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{t(`${fp}.listSubheading`)}</p>
          </div>
          <div className="flex items-center gap-1" role="group" aria-label={t(`${fp}.statusFilterLabel`)}>
            {STATUS_TABS.map((tab) => (
              <Button
                key={tab}
                size="sm"
                variant={tab === status ? 'default' : 'outline'}
                aria-pressed={tab === status}
                onClick={() => {
                  selectStatus(tab);
                }}
              >
                {t(`${fp}.statusLabel.${tab}`)}
              </Button>
            ))}
          </div>
        </header>

        {showUserFilter && (
          <div className="max-w-sm space-y-1.5">
            <label
              htmlFor="tenant-identity-user-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t(`${fp}.userFilterLabel`)}
            </label>
            <UserPicker
              id="tenant-identity-user-filter"
              value={userFilter}
              onChange={setUserFilter}
              options={userResults}
              isLoading={userSearching}
              query={userQuery}
              onQueryChange={setUserQuery}
              placeholder={t(`${fp}.userFilterPlaceholder`)}
              ariaLabel={t(`${fp}.userFilterLabel`)}
            />
          </div>
        )}

        {query.isPending && (
          <div className="space-y-2" aria-hidden="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        )}

        {query.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={query.error}
              heading={t(`${fp}.error.heading`)}
              message={t(`${fp}.error.message`)}
              codeLabel={t(`${fp}.error.codeLabel`)}
              onRetry={() => {
                query.refetch().catch(() => undefined);
              }}
              retryLabel={t(`${fp}.error.retry`)}
            />
          </div>
        )}

        {query.isSuccess && rows.length === 0 && (
          <p className="text-sm text-muted-foreground" role="status">
            {t(`${fp}.empty.${status}`)}
          </p>
        )}

        {query.isSuccess && rows.length > 0 && (
          <DataTable aria-label={t(`${fp}.heading`)}>
            <FederatedIdentitiesTable
              rows={rows}
              userHref={NO_USER_HREF}
              onLink={(row) => {
                link.reset();
                setLinkTarget(row);
              }}
              onUnlink={(row) => {
                unlink.reset();
                setUnlinkTarget(row);
              }}
              pendingUnlinkGuid={unlink.isPending && unlinkTarget !== null ? unlinkTarget.guid : null}
            />
          </DataTable>
        )}
      </section>

      <LinkIdentityDialog
        open={linkTarget !== null}
        identity={linkTarget}
        useUserSearch={useTenantLinkPickerSearch}
        onOpenChange={(next) => {
          if (!next) closeLink();
        }}
        onSubmit={(userGuid) => {
          if (linkTarget === null) return;
          link.mutate({ guid: linkTarget.guid, userGuid }, { onSuccess: closeLink });
        }}
        pending={link.isPending}
        submitError={link.error}
      />

      <DeleteAuthConfirmDialog
        open={unlinkTarget !== null}
        title={t(unlinkTarget?.status === 'PENDING' ? `${fp}.dismiss.title` : `${fp}.unlink.title`)}
        message={t(
          unlinkTarget?.status === 'PENDING' ? `${fp}.dismiss.message` : `${fp}.unlink.message`,
          { identity: unlinkLabel },
        )}
        cancelLabel={t(`${fp}.unlink.cancel`)}
        confirmLabel={t(
          unlinkTarget?.status === 'PENDING' ? `${fp}.dismiss.confirm` : `${fp}.unlink.confirm`,
        )}
        onCancel={() => {
          if (unlink.isPending) return;
          closeUnlink();
        }}
        onConfirm={() => {
          if (unlinkTarget === null) return;
          unlink.mutate(unlinkTarget.guid, { onSuccess: closeUnlink });
        }}
        pending={unlink.isPending}
        submitError={unlink.error}
        submitErrorLabel={(code) => t(`${fp}.unlink.submitError`, { code })}
      />
    </LinkPickerTenantContext.Provider>
  );
}
